import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import OrganizationDeviceTypeModel from '@shared/models/organizations/OrganizationDeviceTypeModel';
import IResult from '@shared/models/result/IResult';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import EditDeviceTypeContainer, { DeviceTypeEditorActions } from './state/EditDeviceTypeContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';

export function useCurrentOrgDeviceTypeEditor(initialModel?: DeviceTypeModel | OrganizationDeviceTypeModel | null) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { deviceId } = useParams();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const { updateOrgDeviceType, createOrgDeviceType } = OrganizationContainer.useContainer();
    const editDeviceTypeContainer = EditDeviceTypeContainer.useContainer();

    useEffect(() => {
        if (
            (deviceId &&
                deviceId == editDeviceTypeContainer.editDeviceTypeRef.current?.id &&
                !editDeviceTypeContainer.hasChangesRef.current) ||
            !initialModel
        ) {
            return;
        }
        editDeviceTypeContainer.initializeEditor(initialModel, organizationId);
        editDeviceTypeContainer.initializeEditListing(organizationId, deviceId);
    }, [deviceId, organizationId, initialModel]);

    const actions: DeviceTypeEditorActions = {
        updateOrgDeviceType,
        createOrgDeviceType,
    };

    const handleAlerts = (result: IResult<DeviceTypeModel>) => {
        if (result.resultType === 'Ok') {
            showSuccess(t('common:saved'));
        } else {
            showError(t('common:saveError'));
        }
    };

    const handleSave = async () => {
        if (!organizationId) throw new Error('Missing organization ID');
        return await editDeviceTypeContainer.handleSave(actions, organizationId, deviceId);
    };

    const handleSaveWithAlerts = async () => {
        const result = await handleSave();
        handleAlerts(result);
        return result;
    };

    const handleCreateAndClearEditor = async () => {
        if (!organizationId) throw new Error('Missing organization ID');
        const result = await editDeviceTypeContainer.handleCreateAndClearEditor(actions, organizationId);
        handleAlerts(result);
        return result;
    };

    const resetEditor = () => {
        editDeviceTypeContainer.initializeEditor(null, organizationId);
    };

    return {
        handleSave,
        handleSaveWithAlerts,
        handleCreateAndClearEditor,
        handleInputChange: editDeviceTypeContainer.handleInputChange,
        editDeviceType: editDeviceTypeContainer.editDeviceType,
        resetEditor,
    };
}
